import React, { useState, useEffect } from 'react';
import { Sparkles, Zap, BookOpen, Clock, Activity } from 'lucide-react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { FeedEvent } from '../types';

export const LiveActivityFeed: React.FC = () => {
  const [events, setEvents] = useState<FeedEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    try {
      const colRef = collection(db, 'feedEvents');
      const q = query(colRef, where('visibility', '==', 'public'), orderBy('timestamp', 'desc'), limit(20));

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const loaded: FeedEvent[] = [];
          snapshot.forEach((docSnap) => {
            const data = docSnap.data() as FeedEvent;
            loaded.push({ id: docSnap.id, ...data });
          });
          setEvents(loaded);
          setLoading(false);
        },
        (error) => {
          console.warn('LiveActivityFeed: fallback query on error', error);
          // Attempt simple query fallback if composite index is generating
          try {
            const simpleQ = query(collection(db, 'feedEvents'), limit(20));
            onSnapshot(simpleQ, (snap) => {
              const fallbackEvents: FeedEvent[] = [];
              snap.forEach((d) => {
                const data = d.data() as FeedEvent;
                if (data.visibility === 'public') {
                  fallbackEvents.push({ id: d.id, ...data });
                }
              });
              setEvents(fallbackEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
              setLoading(false);
            }, () => {
              setLoading(false);
            });
          } catch {
            setLoading(false);
          }
        }
      );

      return () => unsubscribe();
    } catch (err) {
      console.warn('LiveActivityFeed: Initialization error', err);
      setLoading(false);
    }
  }, []);

  // Strict constraint: If the collection is empty or still loading with 0 events, render nothing.
  if (loading || events.length === 0) {
    return null;
  }

  const formatTimeAgo = (isoDate: string) => {
    try {
      const diffMs = Date.now() - new Date(isoDate).getTime();
      const mins = Math.floor(diffMs / 60000);
      if (mins < 1) return 'Just now';
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.floor(hrs / 24);
      return `${days}d ago`;
    } catch {
      return '';
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'content_added':
        return <Sparkles className="w-3.5 h-3.5 text-indigo-600" />;
      case 'tip':
        return <Zap className="w-3.5 h-3.5 text-amber-600" />;
      default:
        return <BookOpen className="w-3.5 h-3.5 text-emerald-600" />;
    }
  };

  // The ticker track is exactly two back-to-back copies of the same item
  // list -- that's what makes a CSS translateX(-50%) loop seamless (the
  // animation resets from -50% to 0% exactly as the second copy lines up
  // with where the first one started). A short event list just means the
  // pattern repeats sooner; it still loops cleanly either way.
  const baseItems = events.slice(0, 12);
  const laneItems = [...baseItems, ...baseItems];

  const renderTickerItem = (item: FeedEvent, key: string) => (
    <div
      key={key}
      className="shrink-0 flex items-center gap-2.5 bg-white border border-slate-200 rounded-full pl-3 pr-4 py-2 mx-2"
    >
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-[10px] font-bold text-indigo-700 shrink-0">
        {getEventIcon(item.type)}
        {item.examCode || 'EXAM PREP'}
      </span>
      <span className="text-xs font-semibold text-slate-800 whitespace-nowrap">
        {item.title}
      </span>
      <span className="text-[10px] text-slate-500 flex items-center gap-1 font-mono whitespace-nowrap shrink-0">
        <Clock className="w-3 h-3 text-slate-500" />
        {formatTimeAgo(item.timestamp)}
      </span>
    </div>
  );

  // Full-bleed, left-to-right, and fitted directly against the bottom of
  // LandingPage.tsx's hero section with no gap (per Peter's 2026-10-05
  // request) -- same "relative left-1/2 ... w-screen" edge-to-edge trick
  // the hero uses, with square top corners so the two sections read as one
  // continuous band, and no border where it meets the hero above it.
  return (
    <section className="relative left-1/2 right-1/2 -mx-[50vw] w-screen overflow-hidden bg-white text-slate-900 border-t-0 border-x-0 border-b border-slate-200 shadow-sm">
      {/* Inline keyframes for the right-to-left ticker loop. Scoped to this
          component via a unique class name rather than touching the global
          index.css, so the diff stays self-contained to this file. */}
      <style>{`
        @keyframes praxisLiveFeedScroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .praxis-live-feed-track {
          animation: praxisLiveFeedScroll 40s linear infinite;
        }
        .praxis-live-feed-track:hover {
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .praxis-live-feed-track {
            animation: none;
          }
        }
      `}</style>

      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-4 sm:px-5 pt-3.5 pb-2.5 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
          </span>
          <h3 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
            <Activity className="w-4 h-4 text-indigo-600" />
            Live Platform & Content Updates
          </h3>
        </div>
        <span className="text-[11px] font-semibold text-slate-500 hidden sm:inline">
          Real-Time Activity
        </span>
      </div>

      {/* Right-to-left auto-scrolling ticker. The lane is twice the visible
          content (laneItems already repeats the base list); animating exactly
          -50% of its own width loops seamlessly with no visible jump. */}
      <div className="relative py-3.5 overflow-hidden">
        <div className="flex w-max praxis-live-feed-track">
          {laneItems.map((item, idx) => renderTickerItem(item, `${item.eventId || item.id}-${idx}`))}
        </div>
        {/* Edge fades so items don't hard-cut against the section border */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-white to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent" />
      </div>
    </section>
  );
};
